import { useState, useEffect } from "react";
import "./SymptomCollector.css";
import arrowIcon from "./../../assets/images/shaded-arrow.svg";
import * as aiBuble from "./../../assets/animations/ai-buble.json";
import * as chakra from "./../../assets/animations/tatva_ai_chakra.json";
import DDxInputSelectorBg from "./../../assets/images/ddx-input-selector-bg.webp";
import arrowUp from "./../../assets/animations/arrow.json";
import arrow from "./../../assets/images/arrow.svg";
import language from "./../../assets/images/language.svg";
import mic from "./../../assets/images/mic-white-filled.svg";
import alert from "./../../assets/images/alert-triangle.svg";
import chevronRight from "./../../assets/images/chevron-right.svg";
import Lottie from "lottie-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_AGENT_NAME,
  FROM,
  JWT_TOKEN,
  LangSpecificMsg,
  LANGUAGE,
  languageOptions,
  MOBILE_NUMBER,
  TYPE,
} from "./utils/constants";
import { fetchAppointmentDetails, fetchSCSummary } from "./service";
import { ValidLanguages } from "./utils/types";
import LoginPopup from "./components/loginPopup/LoginPopup";
import { getDecodedSCToken, interpolate } from "./symptomCollectorHelper";
import { getLocalStorage } from "../../utils/localStorage";
import { fetchAvatars, getReceptionistAgents } from "../appointmentAgent/service";
import { Spin } from "antd";
import { trackEvent } from "../../utils/helper";
import dayjs from "dayjs";
import LoginFullPage from "./components/loginPopup/LoginFullPage";
import SelectLanguageDrawer from "../appointmentAgent/components/selectLanguage/SelectLanguageDrawer";
import agentBg from "./../../assets/images/agent-bg.svg";
import speak from "./../../assets/images/speak.svg";
import secureIcon from "./../../assets/images/secure.svg";


const SymptomCollector = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const [isSCLoggedIn, setIsSCLoggedIn] = useState(
    getLocalStorage(MOBILE_NUMBER)
  );
  const [avatarImage, setAvatarImage] = useState<string>("");

  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
    state?.selectedLang || getLocalStorage(LANGUAGE) || null
  );
  const [isAppointmentInQueue, setIsAppointmentInQueue] =
    useState<boolean>(true);
  const [agentDetails, setAgentDetails] = useState<any>({});

  const type = sessionStorage.getItem(TYPE);
  const from = sessionStorage.getItem(FROM);

  const LangSpecificMsgData =
    LangSpecificMsg[(selectedLanguage as ValidLanguages) || "english"];
  const jwtToken = new URLSearchParams(location.search).get(JWT_TOKEN) || "";
  const decodedToken: any = getDecodedSCToken(jwtToken);

  const {
    um_id,
    patient_unique_id,
    hm_id,
    pam_id,
    patient_name,
    patient_contact_no,
    doctor_name,
    agent_name,
    agent_id,
    dp_name,
    dp_id,
  } = decodedToken?.user || {};

  const [phoneNumber, setPhoneNumber] = useState(patient_contact_no);
  const [isLoading, setIsLoading] = useState(true);

  // Common tracking attributes for all events
  const commonTrackingAttributes = {
    appointment_id: pam_id,
    hospital_id: hm_id,
    doctor_name: doctor_name,
    doctor_id: um_id,
    patient_id: patient_unique_id,
    patient_name: patient_name,
    patient_mobile_number: patient_contact_no,
    doctor_speciality: dp_name,
    dp_id: dp_id,
    timestamp: dayjs().format("YYYY-MM-DD HH:mm:ss"),
  };

  useEffect(() => {
    const hasTrackedLanding = sessionStorage.getItem("hasTrackedSCLanding");

    if (!hasTrackedLanding) {
      trackEvent("SC_Patient_Collector_Landed", commonTrackingAttributes);
      sessionStorage.setItem("hasTrackedSCLanding", "true");
    }
  }, []);

  useEffect(() => {
    const fetchReceptionistAgents = async () => {
      const receptionistAgentDetailsRes = await getReceptionistAgents(
        decodedToken?.user?.hm_id
      );
      const decodedTokenRes: any = getDecodedSCToken(
        receptionistAgentDetailsRes
      );
      if (decodedTokenRes?.user) {
        setAgentDetails(decodedTokenRes?.user);
        const res = await fetchAvatars();
        const avatar = res?.find(
          (item: any) => item?._id === decodedTokenRes?.user?.agent_id
        );
        setAvatarImage(avatar?.lookUrl);
      }
    };
    if (agentDetails && Object.keys(agentDetails).length === 0) {
      fetchReceptionistAgents();
    }
  }, [isSCLoggedIn, agentDetails]);

  useEffect(() => {
    getAppointmentDetails();
    getAvatarImage();
  }, []);

  useEffect(() => {
    if (type) {
      if (avatarImage) {
        sessionStorage.removeItem(TYPE);
        navigate(`/conversation?jwtToken=${jwtToken}`, {
          state: {
            selectedLang: selectedLanguage,
            type: type,
            avatarImage: avatarImage,
          },
        });
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [type, avatarImage]);

  useEffect(() => {
    if (location.state?.from !== "summary" && isSCLoggedIn && avatarImage) {
      getConversationId();
    }
  }, [isSCLoggedIn, avatarImage]);

  const getAvatarImage = async () => {
    const res = await fetchAvatars();
    const avatar = res?.find((item: any) => item?._id === agent_id);
    setAvatarImage(avatar?.lookUrl || res?.[0]?.lookUrl);
  };

  const getAppointmentDetails = async () => {
    const appointmentDetails = await fetchAppointmentDetails(pam_id, jwtToken);
    if (appointmentDetails?.statusCode === "0") {
      setIsAppointmentInQueue(true);
    } else {
      setIsAppointmentInQueue(false);
    }
  };

  const getConversationId = async () => {
    const summaryResponse = await fetchSCSummary(
      {
        um_id: um_id,
        pam_id: pam_id,
        hm_id: hm_id,
        patient_unique_id: patient_unique_id,
      },
      jwtToken
    );
    if (summaryResponse && Object.keys(summaryResponse).length > 0) {
      navigate(`/symptoms-collector-summary?jwtToken=${jwtToken}`, {
        state: {
          summaryResponse: summaryResponse,
          id: summaryResponse?._id,
          selectedLang: summaryResponse?.language,
          isAppointmentInQueue: isAppointmentInQueue,
          conversation: summaryResponse?.conversation,
          avatarImage: avatarImage,
          from: "conversation",
        },
      });
    }
  };

  const handleLanguageSelect = (language: string) => {
    setSelectedLanguage(language);
    trackEvent("SC_Patient_SelectedLanguage", {
      ...commonTrackingAttributes,
      language: language,
    });
  };

  return (
    <div className="App symptom-collector relative min-h-screen">
      {/* Background Image */}
      {isSCLoggedIn && (
        <div className="fixed inset-0">
          <img
            src={DDxInputSelectorBg}
            alt="bg"
            className="h-full w-full object-cover opacity-5"
          />
        </div>
      )}

      {/* Main Content */}
      {isLoading ? (
        <div className="flex justify-center items-center h-[80vh] mt-auto">
          <Spin size="large" />
        </div>
      ) : (
        <div className={isSCLoggedIn ? "relative z-10 p-[18px]" : ""}>
          <div className="flex justify-between">
            {
              <div className="flex items-center gap-2">
                {from === "home-appointment" && (
                  <img
                    loading="lazy"
                    src={chevronRight}
                    alt="chevron-right"
                    className="object-contain shrink-0 self-stretch my-auto w-5 aspect-square rotate-180 cursor-pointer"
                    onClick={() => {
                      navigate(-1);
                    }}
                  />
                )}
                {isSCLoggedIn && (
                  <div className="text-base font-semibold">Add Symptoms</div>
                )}
              </div>
            }
            {selectedLanguage ? (
              <div
                className="flex gap-2 text-xs items-center capitalize p-2 border border-[#4B4AD5] text-[#4B4AD5] rounded-full cursor-pointer h-fit"
                onClick={() => {
                  setSelectedLanguage(null);
                }}
              >
                <img
                  src={language}
                  alt="close-visit"
                  style={{ height: 16, width: 16 }}
                />
                {LangSpecificMsgData.lable}
                <img
                  src={arrow}
                  alt="close-visit"
                  style={{ height: 14, width: 14 }}
                />
              </div>
            ) : null}
          </div>

          {isAppointmentInQueue ? (
            <>
              {!isSCLoggedIn && !(window as any)?.ReactNativeWebView ? (
                <LoginFullPage
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                  isSCLoggedIn={isSCLoggedIn}
                  setIsSCLoggedIn={setIsSCLoggedIn}
                  agentDetails={agentDetails}
                  setSelectedLanguage={setSelectedLanguage}
                />
              ) : (
                <>
                  {/* {!selectedLanguage && (
                    <div className="title-select">
                      Select language to proceed
                    </div>
                  )} */}

                  {!selectedLanguage ? (
                    <SelectLanguageDrawer
                      selectedLanguage={selectedLanguage}
                      setSelectedLanguage={setSelectedLanguage}
                    />
                  ) : (
                    // <div className="language-cards">
                    //   {languageOptions.map((lang) => (
                    //     <div
                    //       key={lang.value}
                    //       className="language-card"
                    //       onClick={() => handleLanguageSelect(lang.value)}
                    //     >
                    //       <div className="card-content">
                    //         <div className="text-content">
                    //           <h2 className="language-name pl-6">
                    //             {lang.label}
                    //           </h2>
                    //         </div>
                    //       </div>
                    //       <img
                    //         src={arrowIcon}
                    //         width={24}
                    //         height={24}
                    //         alt="arrow-icon"
                    //       />
                    //     </div>
                    //   ))}
                    // </div>
                    <div>
                      <div className="recording-content">
                        {/* voice bubble animation */}
                        {/* <div className="controls-container mt-auto flex flex-col items-center gap-4">
                          <>
                            <div
                              className="relative  w-[120px] h-[120px] rounded-full"
                              style={{ background: "rgba(131, 41, 241, 0.1)" }}
                            >
                              <img
                                style={{
                                  position: "absolute",
                                  top: "56%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 10,
                                  width: "90%",
                                  borderRadius: "50px",
                                }}
                                src={avatarImage}
                                alt="client"
                              />
                            </div>
                            <div className="flex flex-col items-center">
                              <div className="text-xl font-semibold">
                                {interpolate(LangSpecificMsgData?.mira, {
                                  name: agent_name ?? DEFAULT_AGENT_NAME,
                                })}
                              </div>
                              <div className="text-xl font-semibold">
                                {LangSpecificMsgData.doctor}
                              </div>
                            </div>
                            <div className="text-[#454551] text-center">
                              {LangSpecificMsgData.aboutSymptoms}
                            </div>
                            <div
                              className="relative flex justify-center items-center"
                              style={{ marginTop: "-50px" }}
                              onClick={() => {
                                navigate(`/conversation?jwtToken=${jwtToken}`, {
                                  state: {
                                    selectedLang: selectedLanguage,
                                    type: "voice",
                                    avatarImage: avatarImage,
                                  },
                                });
                                trackEvent("SC_Patient_Input_Mode", {
                                  ...commonTrackingAttributes,
                                  mode: "voice",
                                  language: selectedLanguage,
                                });
                                trackEvent("SC_Patient_SymptomLogStarted", {
                                  ...commonTrackingAttributes,
                                  mode: "voice",
                                  language: selectedLanguage,
                                });
                              }}
                            >
                              <Lottie
                                animationData={aiBuble}
                                autoplay
                                loop
                                className="w-full"
                                style={{
                                  maxWidth: "250px",
                                  width: "100%",
                                }}
                              />
                              <Lottie
                                style={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 10,
                                  width: "35%",
                                  cursor: "pointer",
                                }}
                                animationData={chakra}
                                autoplay
                                loop
                              />
                              <img
                                src={mic}
                                alt="mic"
                                style={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  zIndex: 10,
                                  width: "10%",
                                  cursor: "pointer",
                                }}
                              />
                            </div>
                            <div className="text-[#4B4AD5] mt-[-45px] flex items-center flex-col">
                              <Lottie
                                animationData={arrowUp}
                                style={{
                                  width: 28,
                                  height: 28,
                                  transform: "rotate(180deg)",
                                  marginTop: "-20px",
                                }}
                                autoplay
                                loop
                              />
                              {LangSpecificMsgData.tapToTalk}
                            </div>

                            <div className="flex items-center w-[30%]">
                              <div className="flex-1 h-[2px] bg-gradient-to-l from-gray-300 to-transparent" />
                              <span className="px-4 text-gray-400">or</span>
                              <div className="flex-1 h-[2px] bg-gradient-to-r from-gray-300 to-transparent" />
                            </div>

                            <div
                              className="relative w-[100%] h-[52px] cursor-pointer"
                              style={{
                                background: `url(${DDxInputSelectorBg})`,
                                backgroundSize: "cover",
                                padding: "2px",
                                borderRadius: "12px",
                              }}
                              onClick={() => {
                                navigate(`/conversation?jwtToken=${jwtToken}`, {
                                  state: {
                                    selectedLang: selectedLanguage,
                                    type: "chat",
                                    avatarImage: avatarImage,
                                  },
                                });
                                trackEvent("SC_Patient_Input_Mode", {
                                  ...commonTrackingAttributes,
                                  mode: "chat",
                                  language: selectedLanguage,
                                });
                                trackEvent("SC_Patient_SymptomLogStarted", {
                                  ...commonTrackingAttributes,
                                  mode: "chat",
                                  language: selectedLanguage,
                                });
                              }}
                            >
                              <div className="absolute inset-[2px] bg-white rounded-[10px] flex items-center justify-center">
                                <span className="w-full h-full px-4 bg-transparent text-center outline-none rounded-[10px] placeholder-gray-400 flex items-center justify-center text-[#98A2B3]">
                                  Type your symptoms here
                                </span>
                              </div>
                            </div>
                          </>
                        </div> */}

                        <div>
                          <div className="recording-content">
                            <div className="controls-container my-auto flex flex-col items-center gap-4">
                              <>
                                <div
                                  className="relative w-[120px] h-[120px] rounded-full"
                                  style={{
                                    background:
                                      "linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(240, 240, 255, 0.8))",
                                    boxShadow: `
                                      0px 0px 30px 10px rgba(250, 248, 253, 1),
                                      0px 0px 60px 20px rgba(250, 248, 253, 1),
                                      inset 0px -40px 60px -20px rgba(250, 248, 253, 1),
                                      inset 0px 20px 40px -10px rgba(250, 248, 253, 1)
                                    `,
                                  }}
                                >
                                  {/* Background image layer with gradient mask */}
                                  <div
                                    className="absolute inset-0 rounded-full overflow-hidden"
                                    style={{
                                      backgroundImage: `url(${agentBg})`,
                                      backgroundSize: "cover",
                                      backgroundPosition: "center",
                                      opacity: 0.85,
                                      zIndex: 0,
                                      maskImage:
                                        "radial-gradient(circle at center, black 40%, transparent 100%)",
                                      WebkitMaskImage:
                                        "radial-gradient(circle at center, black 40%, transparent 100%)",
                                    }}
                                  ></div>

                                  {/* Foreground content */}
                                  <div className="relative z-10 w-full h-full flex items-center justify-center overflow-hidden rounded-full">
                                    {avatarImage && (
                                      <img
                                        src={avatarImage}
                                        alt="avatar"
                                        style={{
                                          position: "absolute",
                                          top: "56%",
                                          left: "50%",
                                          transform: "translate(-50%, -50%)",
                                          width: "90%",
                                          borderRadius: "50%",
                                          zIndex: 10,
                                          maskImage:
                                            "linear-gradient(to bottom, black 60%, transparent 100%)",
                                          WebkitMaskImage:
                                            "linear-gradient(to bottom, black 60%, transparent 100%)",
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="text-3xl font-bold"
                                    style={{ fontFamily: "Playfair Display" }}
                                  >
                                    Hi,
                                  </div>
                                  <div
                                    className="text-3xl font-bold"
                                    style={{ fontFamily: "Playfair Display" }}
                                  >
                                    {interpolate(LangSpecificMsgData?.mira, {
                                      name: agentDetails?.agent_name,
                                    })}
                                  </div>
                                </div>
                                <div className="text-[#454551] text-center text-sm">
                                  {LangSpecificMsgData.aboutSymptoms}
                                </div>

                                <div className="fixed bottom-14 left-1/2 transform -translate-x-1/2 w-full max-w-[450px] px-[18px] flex justify-between items-center gap-2">
                                  <div
                                    className="w-[70%] h-[52px] cursor-pointer relative"
                                    style={{
                                      background: `url(${DDxInputSelectorBg})`,
                                      backgroundSize: "cover",
                                      padding: "2px",
                                      borderRadius: "12px",
                                      flex: 1,
                                    }}
                                    onClick={() => {
                                      navigate(
                                        `/conversation?jwtToken=${jwtToken}`,
                                        {
                                          state: {
                                            selectedLang: selectedLanguage,
                                            type: "chat",
                                            avatarImage: avatarImage,
                                          },
                                        }
                                      );
                                      trackEvent("SC_Patient_Input_Mode", {
                                        ...commonTrackingAttributes,
                                        mode: "chat",
                                        language: selectedLanguage,
                                      });
                                      trackEvent(
                                        "SC_Patient_SymptomLogStarted",
                                        {
                                          ...commonTrackingAttributes,
                                          mode: "chat",
                                          language: selectedLanguage,
                                        }
                                      );
                                    }}
                                  >
                                    <div className="absolute inset-[2px] bg-white rounded-[10px] flex items-center justify-center">
                                      <span className="w-full h-full px-4 bg-transparent text-center outline-none rounded-[10px] placeholder-gray-400 flex items-center text-[#98A2B3] text-sm">
                                        {LangSpecificMsgData?.tapToTalk}
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      background:
                                        "linear-gradient(180deg, #9A7DDA 0%, #2F2EA4 100%)",
                                      borderRadius: "12px",
                                      height: 52,
                                    }}
                                    className="flex items-center justify-center gap-2 cursor-pointer"
                                    onClick={() => {
                                      navigate(
                                        `/conversation?jwtToken=${jwtToken}`,
                                        {
                                          state: {
                                            selectedLang: selectedLanguage,
                                            type: "voice",
                                            avatarImage: avatarImage,
                                          },
                                        }
                                      );
                                      trackEvent("SC_Patient_Input_Mode", {
                                        ...commonTrackingAttributes,
                                        mode: "voice",
                                        language: selectedLanguage,
                                      });
                                      trackEvent(
                                        "SC_Patient_SymptomLogStarted",
                                        {
                                          ...commonTrackingAttributes,
                                          mode: "voice",
                                          language: selectedLanguage,
                                        }
                                      );
                                    }}
                                  >
                                    <div className="relative p-[14px] rounded-[12px] h-[52px] backdrop-blur-[40%] flex items-center justify-center gap-2 cursor-pointer">
                                      {/* Background image layer */}
                                      <div
                                        className="absolute inset-0 rounded-[12px] opacity-60"
                                        style={{
                                          backgroundImage: `url(${DDxInputSelectorBg})`,
                                          backgroundSize: "cover",
                                          backgroundPosition: "center",
                                          zIndex: 0,
                                        }}
                                      ></div>

                                      {/* Foreground content */}
                                      <div className="relative z-10 flex items-center justify-center gap-2">
                                        <img
                                          src={speak}
                                          alt="speak"
                                          className="w-[24px] h-[24px]"
                                        />
                                        <span className="text-sm text-white font-semibold">
                                          Speak
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </>
                            </div>
                          </div>
                        </div>
                        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 w-full max-w-[450px] px-[18px] flex justify-between items-center gap-2 m-auto mix-blend-luminosity">
                          <div className="flex gap-2 justify-center opacity-40 m-auto">
                            <img src={secureIcon} alt="secure-icon" />
                            <div className="text-xs text-[#454551]">
                              Your Data is Secured & only doctor can access it
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
              <div>
                <div className="flex flex-col items-center gap-2">
                  <img src={alert} alt="alert" />
                  <div className="text-[#1F2933] text-xl font-semibold mt-8">
                    Can't Add Symptoms Now
                  </div>
                  <div className="text-[#3D4854] text-center max-w-md">
                    It looks like your consultation with {doctor_name} has
                    already started or finished. You can't add symptoms at this
                    time.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SymptomCollector;
